// Upload Method A, carried over unchanged from the Phase 0 spike (experiments/measurement-spike/src/page/upload-ab.ts)
// and validated in docs/measurement-research.md §16–§19. Do not change the method without a new validation.
import { MeasurementError, type UploadRaw, type UploadTimelineEntry } from './types';

/**
 * - 4 parallel request loops (staggered 50 ms), each POSTing bodies back to back (sequential per stream).
 * - Each loop starts at 64 KiB and doubles its body (max 16 MiB) after any request faster than 300 ms.
 * - The first 1 s is ramp-up; throughput is measured over the following 4 s window.
 * - No new requests start after the window; in-flight ones finish.
 * - Only completed requests count, each contributing the share of its bytes whose duration falls inside the window
 *   (assumes a constant rate within a request). Upload progress events are not used (unreliable in Safari).
 * - Bodies are Blob slices: Chromium spends ~1 s per 16 MiB copying string/ArrayBuffer bodies before sending.
 */
export interface UploadMethod {
  streams: number;
  streamStaggerMs: number;
  rampUpMs: number;
  windowMs: number;
  initialChunkBytes: number;
  maxChunkBytes: number;
  growIfFasterThanMs: number;
}

export const UPLOAD_METHOD_A = {
  streams: 4,
  streamStaggerMs: 50,
  rampUpMs: 1000,
  windowMs: 4000,
  // Was 512 KiB until 2026-10-11: on slow uplinks no 512 KiB request finished inside the window. 64 KiB read
  // higher on Android Verizon cellular (5/6 pairs, median ×1.38) and equal on fast Wi-Fi (median ×1.00).
  initialChunkBytes: 64 * 1024,
  maxChunkBytes: 16 * 1024 * 1024,
  growIfFasterThanMs: 300,
} as const satisfies UploadMethod;

export interface CompletedRequest {
  startMs: number;
  endMs: number;
  bytes: number;
}

export function bytesInWindow(requests: CompletedRequest[], windowStart: number, windowEnd: number): number {
  let total = 0;
  for (const r of requests) {
    const duration = r.endMs - r.startMs;
    if (duration <= 0) continue;
    const overlap = Math.min(r.endMs, windowEnd) - Math.max(r.startMs, windowStart);
    if (overlap > 0) total += (r.bytes * overlap) / duration;
  }
  return total;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

let uploadBlob: Blob | undefined;

export async function measureUpload(
  url: string,
  options: { signal?: AbortSignal; method?: UploadMethod } = {},
): Promise<UploadRaw> {
  const m = options.method ?? UPLOAD_METHOD_A;
  const { signal } = options;
  if (!uploadBlob || uploadBlob.size < m.maxChunkBytes) {
    uploadBlob = new Blob([new Uint8Array(m.maxChunkBytes)], { type: 'text/plain' });
  }
  const body = uploadBlob;
  // The default resource-timing buffer (~250 entries) fills quickly, after which protocols go unreported.
  performance.clearResourceTimings();
  performance.setResourceTimingBufferSize(2000);
  const t0 = performance.now();
  const windowStart = t0 + m.rampUpMs;
  const windowEnd = windowStart + m.windowMs;
  const completed: CompletedRequest[] = [];
  const timeline: UploadTimelineEntry[] = [];
  const errors: string[] = [];
  let maxChunk = 0;

  const loop = async (index: number) => {
    await sleep(index * m.streamStaggerMs);
    let size: number = m.initialChunkBytes;
    while (performance.now() < windowEnd && errors.length === 0 && !signal?.aborted) {
      const startMs = performance.now();
      try {
        const response = await fetch(url, {
          method: 'POST',
          body: body.slice(0, size, 'text/plain'),
          headers: { 'Content-Type': 'text/plain' },
          cache: 'no-store',
          credentials: 'omit',
          signal,
        });
        await response.arrayBuffer();
        const endMs = performance.now();
        if (response.status !== 204) {
          errors.push(`HTTP ${response.status} on ${size} B`);
          return;
        }
        completed.push({ startMs, endMs, bytes: size });
        timeline.push({ stream: index, startMs: startMs - t0, endMs: endMs - t0, bytes: size });
        maxChunk = Math.max(maxChunk, size);
        if (endMs - startMs < m.growIfFasterThanMs && size < m.maxChunkBytes) size *= 2;
      } catch (error) {
        errors.push(String(error));
        return;
      }
    }
  };
  await Promise.all(Array.from({ length: m.streams }, (_, i) => loop(i)));

  if (signal?.aborted) {
    throw signal.reason instanceof MeasurementError
      ? signal.reason
      : new MeasurementError('cancelled', 'The check was stopped.', 'upload');
  }

  const attributedBytes = bytesInWindow(completed, windowStart, windowEnd);
  const resolved = new URL(url, globalThis.location?.href ?? 'http://localhost/').href;
  const protocols = [
    ...new Set(
      performance.getEntriesByName(resolved).map((e) => (e as PerformanceResourceTiming).nextHopProtocol || 'not reported'),
    ),
  ];
  return {
    mbps: errors.length ? null : (attributedBytes * 8) / (m.windowMs / 1000) / 1e6,
    attributedBytes,
    bytesSent: completed.reduce((sum, r) => sum + r.bytes, 0),
    requests: completed.length,
    maxChunkBytes: maxChunk,
    durationMs: performance.now() - t0,
    protocols,
    errors,
    timeline,
  };
}
